// Reusable in-app confirm modal to replace window.confirm().
//
// Usage (imperative via hook — the most ergonomic drop-in for
// handlers that already work like `if (confirm(...)) { ... }`):
//
//   const confirm = useConfirm();
//   // inside a handler:
//   const ok = await confirm({
//     title: "Move to trash?",
//     body: `Move "${file.name}" to Drive trash?`,
//     danger: true,
//   });
//   if (!ok) return;
//
// Mount <ConfirmProvider> once at the app root. It provides the
// queue/modal; the hook returns a promise that resolves to a
// boolean. The queue is serialized so stacking two confirms (e.g.
// two rapid clicks) can't produce a hang — the second shows once
// the first resolves.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { Button } from "./Button";
import { OVERLAY_SHADOW, Z, cx } from "./controlStyles";
import { useFocusTrap, useScrollLock } from "./overlayHooks";

interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** If true, the confirm button is the solid danger button, and focus
   * starts on Cancel. Use for deletes, destructive admin actions, anything
   * with no undo. The dialog's frame is Modal's, so every confirm looks one way. */
  danger?: boolean;
}

type Resolver = (value: boolean) => void;

interface QueueItem extends ConfirmOptions {
  id: number;
  resolve: Resolver;
}

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const nextId = useRef(1);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setQueue((q) => [
          ...q,
          { ...opts, id: nextId.current++, resolve },
        ]);
      }),
    [],
  );

  const value = useMemo<ConfirmContextValue>(
    () => ({ confirm }),
    [confirm],
  );

  const current = queue[0] ?? null;

  function answer(ok: boolean) {
    if (!current) return;
    current.resolve(ok);
    // Shift the queue — if another confirm was requested during
    // the current one, it surfaces now.
    setQueue((q) => q.slice(1));
  }

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {current && <ConfirmModal key={current.id} item={current} onAnswer={answer} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback to window.confirm if the provider isn't mounted —
    // never crash a handler over a missing modal context. Safe to
    // use the sync native confirm inside an async wrapper.
    return async (opts: ConfirmOptions) => {
      const msg =
        typeof opts.body === "string"
          ? opts.body
          : opts.title;
      return window.confirm(msg);
    };
  }
  return ctx.confirm;
}

function ConfirmModal({
  item,
  onAnswer,
}: {
  item: QueueItem;
  onAnswer: (ok: boolean) => void;
}) {
  const danger = !!item.danger;
  const panel = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useScrollLock(true);
  // A destructive confirm opens on Cancel (the safe answer); an ordinary
  // one on its confirm button.
  useFocusTrap(panel, true, danger ? cancelRef : confirmRef);

  // Escape = cancel. Enter answers with the FOCUSED button only: Enter on
  // the confirm button confirms, on Cancel cancels, anywhere else nothing
  // — so a stray or double Enter never deletes. Key repeats are ignored
  // (holding Enter on the button that opened the dialog must not run
  // straight into it). Captured on window so a dialog underneath does not
  // also react to the same key.
  const answerRef = useRef(onAnswer);
  answerRef.current = onAnswer;
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (!e.repeat) answerRef.current(false);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (e.repeat) return;
        if (document.activeElement === confirmRef.current) answerRef.current(true);
        else if (document.activeElement === cancelRef.current) answerRef.current(false);
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-[color:var(--c-scrim)] p-4 backdrop-blur-sm"
      style={{ zIndex: Z.confirm }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onAnswer(false);
      }}
    >
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={item.body !== undefined ? bodyId : undefined}
        tabIndex={-1}
        className={cx("flex w-full max-w-[440px] flex-col rounded-card border border-line bg-overlay outline-none", OVERLAY_SHADOW)}
        style={{ animation: "fadeIn 120ms ease-out both" }}
      >
        <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
          <h2 id={titleId} className="min-w-0 text-md font-semibold text-fg">
            {item.title}
          </h2>
          <Button variant="ghost" size="icon" onClick={() => onAnswer(false)} aria-label="Close" className="-mr-2 -mt-1">
            <X aria-hidden className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        </div>
        {item.body !== undefined && (
          <div id={bodyId} className="px-5 pb-5 text-base text-fg-muted">
            {item.body}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle px-5 py-3">
          <Button ref={cancelRef} type="button" variant="ghost" onClick={() => onAnswer(false)}>
            {item.cancelLabel ?? "Cancel"}
          </Button>
          <Button
            ref={confirmRef}
            type="button"
            variant={danger ? "danger-solid" : "primary"}
            onClick={() => onAnswer(true)}
          >
            {item.confirmLabel ?? (danger ? "Delete" : "Confirm")}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
